<template>
  <div>
    <!-- 新商品絞り込みチェック -->
    <div class="content" v-if="enableFilterNewItems">
      <md-checkbox v-model="_filterNewItems">
        新商品に絞る
      </md-checkbox>
    </div>
    <gl-tree-view
      :entries="itemCategories"
      :selected="categories"
      v-on:update:selected="$emit('update:categories', $event)"
    />
  </div>
</template>

<script>
import GlTreeView from '@/components/common/GlTreeView.vue'
import ErrorReport from '@/mixins/ErrorReport'
import { collectCodes } from '@/util/list'

/**
 * 「商品モード - 分類選択時」コンポーネント
 *
 * 選択されている分類の一覧は、常に最下層のコード一覧を親コンポーネントと
 * 連動させます。
 */
export default {
  components: { GlTreeView },
  mixins: [ErrorReport],
  props: {
    /**
     * 分類をロードする対象の企業コード
     */
    company: {
      type: String,
    },
    // itemCategoryLevel: {
    //   type: Number,
    // },
    /**
     * 選択されているカテゴリ（最下層）
     */
    categories: {
      type: Array,
      default: () => [],
    },
    /**
     * POSの開示制限あり
     */
    posRestriction: {
      type: Boolean,
      default: false,
    },
    /**
     * 新商品のみに絞り込むかどうかのユーザー選択を有効にするか否か
     *
     * 絞り込みはあくまで分析結果であって、このフラグをOnにしてもツリー表示や
     * 商品検索には影響ありません。
     */
    enableFilterNewItems: {
      type: Boolean,
      default: false,
    },
    /**
     * 分類指定、商品選択での新商品絞り込みユーザー選択値
     */
    filterNewItems: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    company: {
      immediate: true,
      async handler(company, oldCompany) {
        if (company) {
          let categories = this.categories
          if (oldCompany) {
            categories = []
            this.$emit('update:categories', [])
          }

          this.itemCategories = []

          // 企業が決まらないと分類は取得できない
          const categoriesTree = await this.$repositories
            .get('itemCategory')
            .get(company, this.posRestriction)
            .then(categories => [
              {
                code: undefined,
                name: '全分類',
                children: categories,
                open: true,
              },
            ])

          // カテゴリーコードを選択可能なものに絞り込む
          // 手動で変更時（oldCompanyがある場合）はスキップ
          if (!oldCompany) {
            const set = collectCodes(categoriesTree, 'code')
            categories = this.categories.filter(category => set.has(category))
            this.$emit('update:categories', categories)
          }

          if (categories.length > 0) {
            const categoriesSet = new Set(categories)

            walk(categoriesTree[0], 0, entry => {
              if (entry.children) {
                return
              }
              entry.state = categoriesSet.has(entry.code) ? 'checked' : 'none'
            })
          }

          this.itemCategories = categoriesTree
        }
      },
    },
  },
  computed: {
    /**
     * 分類指定、商品選択での新商品絞り込み
     */
    _filterNewItems: {
      get() {
        return this.filterNewItems
      },
      set(newVal) {
        this.$emit('update:filterNewItems', newVal)
      },
    },
    /**
     * 入力エラー
     */
    errors() {
      const errors = []

      if (!this.categories || this.categories.length === 0) {
        errors.push('分類を選択してください')
      }

      return errors
    },
  },
  data() {
    return {
      /**
       * 分類階層レベルマスタ
       */
      itemCategoryLevels: [],
      /**
       * ツリー用の分類の階層構造データ
       */
      itemCategories: [],
    }
  },
}

/**
 * ツリーノードの再帰処理
 */
function walk(entry, level, f) {
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
  f(entry, level)
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.line {
  margin-top: 32px;
  &:first-child {
    margin-top: 0;
  }
}

.tree-view-container {
  height: 350px;
  overflow: auto;
  border: 1px solid var(--color-border);
  border-radius: 5px;
  padding: var(--spacing-medium);
}
</style>
